import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './inboxIcons';

const INTENT_LABELS = {
  order_status: 'Order status', delay_complaint: 'Delay complaint', not_received: 'Not received',
  damaged_or_wrong: 'Damaged or wrong', colour_mismatch: 'Colour mismatch', return_exchange: 'Return or exchange',
  cancel: 'Cancellation', address_change: 'Address change', refund: 'Refund', payment_issue: 'Payment issue',
  invoice: 'Invoice', wholesale: 'Wholesale', product_or_offer_question: 'Product or offer', international: 'International', other: 'Other',
};

// Status after sending: values the approve endpoint accepts
export const STATUS_AFTER = [
  ['pending', 'Waiting on customer'],
  ['open', 'In progress'],
  ['resolved', 'Resolved'],
];

// Shown next to the status the AI picked
export const AI_PICK = ' · AI pick';

const REDRAFT_CHIPS = ['Shorter', 'More apologetic', 'Less formal', 'Reply in Hinglish', 'Reply in English', "Don't mention dates", "Don't promise anything"];

// AI draft for one conversation: approve, edit in the composer, redraft with a reason, or reject.
function InboxDraft({ api, ticketId, windowChip, refreshKey, onSent, onEdit, onHasDraft, onWriteOwn, toast }) {
  const [draft, setDraft] = useState(undefined);
  const [statusAfter, setStatusAfter] = useState('pending');
  const [busy, setBusy] = useState(null);
  const [panel, setPanel] = useState(null); // 'redraft' | 'reject'
  const [note, setNote] = useState('');
  const [expanded, setExpanded] = useState(true); // full text by default; the arrow folds it to one line

  const load = useCallback(async () => {
    try {
      const data = await api(`/api/v1/support/tickets/${ticketId}/agent-draft`);
      const d = data.draft && data.draft.status === 'pending' ? data.draft : null;
      setDraft(d);
      setStatusAfter(d?.suggestedStatus || 'pending');
    } catch {
      setDraft(null);
    }
  }, [api, ticketId]);

  useEffect(() => { setDraft(undefined); setPanel(null); setNote(''); setExpanded(true); load(); }, [load, refreshKey]);

  // The inbox hides its reply box while a draft is waiting
  useEffect(() => { if (draft !== undefined) onHasDraft?.(!!draft); }, [draft, onHasDraft]);

  const approve = async () => {
    setBusy('approve');
    try {
      const r = await api(`/api/v1/support/agent/drafts/${draft.draftId}/approve`, 'POST', { replyBody: draft.replyBody, status: statusAfter });
      toast(`Sent · ${STATUS_AFTER.find(([v]) => v === (r.status || statusAfter))?.[1] || 'Sent'}`);
      setDraft(null);
      onSent();
    } catch (err) {
      toast(err.message, true);
    } finally {
      setBusy(null);
    }
  };

  const redraft = async (instruction) => {
    setBusy('redraft');
    try {
      await api(`/api/v1/support/tickets/${ticketId}/agent-redraft`, 'POST', { instruction });
      setPanel(null);
      setNote('');
      await load();
      toast('New draft ready');
    } catch (err) {
      toast(err.message, true);
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setBusy('reject');
    try {
      await api(`/api/v1/support/agent/drafts/${draft.draftId}/reject`, 'POST', { reason: note });
      setDraft(null);
      setPanel(null);
      setNote('');
      toast('Draft rejected');
    } catch (err) {
      toast(err.message, true);
    } finally {
      setBusy(null);
    }
  };

  if (draft === undefined) return null;
  if (!draft) {
    return (
      <div className="cx-draft-empty" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => redraft('')} aria-busy={busy === 'redraft'} disabled={!!busy}>
          <Icon name="spark" />{busy === 'redraft' ? 'Drafting…' : 'Draft a reply with AI'}
        </button>
      </div>
    );
  }

  const chosen = note.split(/;\s*/).map((p) => p.trim()).filter(Boolean);
  const toggleChip = (c) => setNote((chosen.includes(c) ? chosen.filter((p) => p !== c) : [...chosen, c]).join('; '));
  const why = [
    draft.triage?.summary,
    ...(draft.toolLog || []).map((l) => (l.found ? `Order ${l.orders.join(', ')} looked up` : `Order lookup: ${l.input?.order_number || 'by email'} not found`)),
    draft.redraftInstruction ? `Rewritten as asked: ${draft.redraftInstruction}` : null,
    draft.fromAlias === 'wholesale' ? 'Signed as Wholesale Team, sent from wholesale@' : null,
    draft.usedOtherChannels ? "Read this customer's recent messages on other channels to stay consistent" : null,
    draft.lessonRules?.length ? `Followed ${draft.lessonRules.length} approved team lesson${draft.lessonRules.length === 1 ? '' : 's'} for this topic` : null,
  ].filter(Boolean);

  const firstLine = String(draft.replyBody || '').split('\n').map((l) => l.trim()).filter(Boolean).find((l) => !/^(hi|hello|dear|namaste)\b/i.test(l)) || '';
  const alerts = [
    draft.needsHuman && draft.humanAction ? { high: draft.humanPriority === 'high', text: `Team action${draft.humanPriority === 'high' ? ' (urgent)' : ''}: ${draft.humanAction}` } : null,
    draft.triage?.mentions_attachment ? { high: false, text: 'Customer mentions an attachment. Check the original message.' } : null,
  ].filter(Boolean);

  return (
    <div className={`cx-draft${expanded ? ' open' : ' folded'}`} aria-label="AI draft reply">
      <div className="cx-draft-head">
        <button className="cx-draft-toggle" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} aria-label={expanded ? 'Fold the AI draft' : 'Read the full AI draft'}>
          <Icon name="down" size={16} className={expanded ? 'cx-rot' : ''} />
          <b><Icon name="spark" size={16} />AI draft</b>
        </button>
        {draft.type === 'follow_up' && <span className="cx-chip cx-c-waiting" title="The customer hasn't replied, so the AI wrote a gentle check-in">Follow-up</span>}
        {draft.triage?.intent && <span className="cx-chip cx-c-plain">{INTENT_LABELS[draft.triage.intent] || draft.triage.intent}</span>}
        {draft.confidence && expanded && <span className="cx-chip cx-c-plain">Confidence {draft.confidence}</span>}
        {draft.triage?.angry && <span className="cx-chip cx-c-new">Upset customer</span>}
        {windowChip}
        <span className="cx-spacer" />
      </div>
      {expanded ? (
        <div className="cx-draft-scroll">
          {alerts.map((a) => <div key={a.text} className={`cx-team-action${a.high ? ' high' : ''}`}>{a.text}</div>)}
          <div className={`cx-draft-body${busy === 'redraft' ? ' loading' : ''}`}>{draft.replyBody}</div>
          {why.length > 0 && (
            <div className="cx-why" aria-label="Why the AI wrote this reply">
              <div className="cx-why-h"><Icon name="info" size={14} />Why the AI wrote this<span>For the team only. Not sent to the customer.</span></div>
              {why.map((w) => <div key={w}><Icon name="check" /><span>{w}</span></div>)}
            </div>
          )}
        </div>
      ) : (
        <button className="cx-draft-peek" onClick={() => setExpanded(true)} title="Read the full draft">
          {alerts.map((a) => <span key={a.text} className={`cx-peek-alert${a.high ? ' high' : ''}`}><Icon name="alert" size={14} />{a.text}</span>)}
          <span className={`cx-peek-text${busy === 'redraft' ? ' loading' : ''}`}>{busy === 'redraft' ? 'Rewriting…' : firstLine}</span>
        </button>
      )}
      {panel === 'redraft' && (
        <>
          <div className="cx-chips-row">
            {REDRAFT_CHIPS.map((c) => (
              <button key={c} type="button" className="cx-chipbtn" aria-pressed={chosen.includes(c)} onClick={() => toggleChip(c)}>{c}</button>
            ))}
          </div>
          <div className="cx-inline-form">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should change? e.g. mention refund in 6 to 9 days" aria-label="Redraft instruction"
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) redraft(note.trim()); }} />
            <button className="cx-btn cx-btn-primary" onClick={() => redraft(note.trim())} disabled={!!busy}>{note.trim() ? 'Redraft' : 'Redraft without note'}</button>
          </div>
        </>
      )}
      {panel === 'reject' && (
        <div className="cx-inline-form">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was wrong? Helps the agent learn" aria-label="Reject reason" />
          <button className="cx-btn" onClick={reject} disabled={!!busy}>{busy === 'reject' ? 'Rejecting…' : 'Reject draft'}</button>
        </div>
      )}
      <div className="cx-draft-foot">
        <button className="cx-btn cx-btn-primary" onClick={approve} disabled={!!busy} aria-busy={busy === 'approve'} data-cx-approve>
          <Icon name="send" />{busy === 'approve' ? 'Sending…' : 'Approve and send'}
        </button>
        <button className="cx-btn" onClick={() => onEdit(draft, statusAfter)} disabled={!!busy} data-cx-edit title="Open the draft in the reply box to edit (E)"><Icon name="edit" />Review and edit</button>
        <button className="cx-btn cx-btn-ghost cx-btn-icon" onClick={() => { setPanel(panel === 'redraft' ? null : 'redraft'); setNote(''); }} disabled={!!busy} aria-label="Redraft" title="Redraft"><Icon name="refresh" /></button>
        <button className="cx-btn cx-btn-ghost cx-btn-icon" onClick={() => { setPanel(panel === 'reject' ? null : 'reject'); setNote(''); }} disabled={!!busy} aria-label="Reject" title="Reject"><Icon name="x" /></button>
        {onWriteOwn && <button className="cx-btn cx-btn-ghost cx-btn-icon" onClick={() => onWriteOwn('note')} disabled={!!busy} aria-label="Internal note" title="Add an internal note"><Icon name="lock" /></button>}
        <span className="cx-then-group">
          <span className="cx-then">then</span>
          <span className="cx-select cx-select-status">
            <select value={statusAfter} onChange={(e) => setStatusAfter(e.target.value)} aria-label="Status after sending">
              {STATUS_AFTER.map(([v, l]) => <option key={v} value={v}>{l}{draft.suggestedStatus === v ? AI_PICK : ''}</option>)}
            </select>
            <Icon name="down" />
          </span>
        </span>
      </div>
    </div>
  );
}

export default InboxDraft;
